
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { Subject } from 'rxjs';
import { PageBaseComponent } from '../../abstract/page-base/page-base.component';
import { SignalComponent } from '../../components/signal/signal.component';
import { Graph } from '../../models/graph';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Parameters } from '../../utilities/parameters';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-personal',
  imports: [
    FormsModule,
    CardModule,
    SignalComponent,
    DatePickerModule,
    ButtonModule,
    DialogModule,
    ListboxModule,
    PageBaseComponent
],
  standalone: true,
  templateUrl: './personal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './personal.component.css'
})
export class PersonalComponent implements OnInit {
  private messageService = inject(UiNotificationService);
  private translate = inject(TranslateService);

  segnaleFreqCardiaca: Graph[][] = [];
  segnaleFreqRespiratoria: Graph[][] = [];
  segnaleRR: Graph[][] = [];
  segnaleSat: Graph[][] = [];
  segnaleTemp: Graph[][] = [];
  loadingFreqCardiaca: boolean = false;
  loadingFreqRespiratoria: boolean = false;
  loadingRR: boolean = false;
  loadingSat: boolean = false;
  loadingTemp: boolean = false;

  xMax: number = -100000;
  xMin: number = 100000;

  startDate: Date = new Date();
  endDate: Date = new Date();

  showDialog: boolean = false;

  availableMeasures: any[] = [
    { id: 1, name: 'parameters.heartrate' },
    { id: 2, name: 'parameters.breathfrequency' },
    { id: 3, name: 'parameters.rr' },
    { id: 4, name: 'parameters.saturation' },
    { id: 5, name: 'parameters.temperature' }
  ];
  selectedMeasures: { id: number; name: string }[] = [];

  zoomLevel: Subject<any> = new Subject<any>();
  zoomCommand: Subject<number> = new Subject<number>();

  // 1. Definiamo le chiavi di traduzione associate alle fasce orarie
  private readonly greetingKeys = [
    'greetings.morning',
    'greetings.afternoon',
    'greetings.evening',
    'greetings.night'
  ];

  // 2. Creiamo un signal reattivo per le traduzioni correnti
  private currentTranslations = toSignal(this.translate.stream(this.greetingKeys), { initialValue: {} as Record<string, string> });

  // 3. Il tuo metodo calcola ora solo la CHIAVE corretta (operazione sincrona e velocissima)
  private getGreetingKey(): string {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) return 'greetings.morning';
    if (hour >= 12 && hour < 18) return 'greetings.afternoon';
    if (hour >= 18 && hour < 22) return 'greetings.evening';
    return 'greetings.night';
  }

  // 4. Espone il titolo finale come Computed Signal pronto per il template
  readonly getTitle = computed<string>(() => {
    const translations = this.currentTranslations();
    const activeKey = this.getGreetingKey();

    // Recupera la stringa tradotta (o usa una stringa vuota temporanea di fallback)
    const baseGreeting = translations[activeKey] || '';

    // Gestione del nome utente
    const userName = Parameters.loggedUser.id !== '' ? Parameters.loggedUser.name : '';

    // Interpoliamo manualmente i parametri all'interno del Computed Signal
    // Ngx-translate usa le doppie graffe: {{name}}
    return baseGreeting.replace('{{name}}', userName).trim();
  });

  constructor(private apiService: ApiService) {
  }

  ngOnInit(): void {
    this.endDate = new Date();
    this.startDate = new Date(this.endDate.getTime());
    this.startDate.setHours(this.startDate.getHours() - 1);

    this.loadMeasures();

    this.loadData();
  }

  getName(id: number): string {
    const found = this.findMeasure(this.availableMeasures, id);
    return found !== undefined ? found.name : `Misura ${id}`;
  }

  isShown(id: number): boolean {
    return this.findMeasure(this.selectedMeasures, id) !== undefined;
  }

  onClose() {
    this.loadMeasures();
    this.showDialog = false;
  }

  onReload() {
    this.loadData();
  }

  onSave() {
    Parameters.configuration.shownMeasures = this.selectedMeasures.map(m => m.id);

    this.apiService.updateConfiguration().subscribe({
      next: (r) => {
        this.showDialog = false;
      },
      error: (err: HttpErrorResponse) => {
        this.showDialog = false;
        this.messageService.showError(err);
      }
    })
  }

  onShowDialog() {
    this.showDialog = true;
  }

  onZoomChanged(scales: any) {
    this.zoomLevel.next(scales);
  }

  onZoomCommand(event: number) {
    this.zoomCommand.next(event)
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (ms < 0) {
        reject(new Error('Delay time cannot be negative'));
      }
      setTimeout(resolve, ms);
    });
  }

  private findMeasure(array: any[], id: number): any {
    return array.find((obj) => { return obj.id === id; })
  }

  private async loadData(): Promise<void> {
    this.setReferenceTime();

    this.loadingFreqCardiaca = true;
    this.loadingFreqRespiratoria = true;
    this.loadingRR = true;
    this.loadingSat = true;
    this.loadingTemp = true;

    // Frequenza Cardiaca
    if (this.isShown(1)) {
      this.apiService.getMeasure(Parameters.loggedUser.id, this.startDate, this.endDate, 0).subscribe({
        next: (r) => {
          this.segnaleFreqCardiaca = r;
          this.loadingFreqCardiaca = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loadingFreqCardiaca = false;
          this.messageService.showError(err);
        }
      })

      await this.delay(200);
    } else {
      this.loadingFreqCardiaca = false;
    }

    // Frequenza Respiratoria
    if (this.isShown(2)) {
      this.apiService.getMeasure(Parameters.loggedUser.id, this.startDate, this.endDate, 1).subscribe({
        next: (r) => {
          this.segnaleFreqRespiratoria = r;
          this.loadingFreqRespiratoria = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loadingFreqRespiratoria = false;
          this.messageService.showError(err);
        }
      })

      await this.delay(200);
    } else {
      this.loadingFreqRespiratoria = false;
    }

    //R-R
    if (this.isShown(3)) {
      this.apiService.getMeasure(Parameters.loggedUser.id, this.startDate, this.endDate, 19).subscribe({
        next: (r) => {
          this.segnaleRR = r;
          this.loadingRR = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loadingRR = false;
          this.messageService.showError(err);
        }
      })

      await this.delay(200);
    } else {
      this.loadingRR = false;
    }

    //Saturazione
    if (this.isShown(4)) {
      this.apiService.getMeasure(Parameters.loggedUser.id, this.startDate, this.endDate, 5).subscribe({
        next: (r) => {
          this.segnaleSat = r;
          this.loadingSat = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loadingSat = false;
          this.messageService.showError(err);
        }
      })

      await this.delay(200);
    } else {
      this.loadingSat = false;
    }

    //Temperatura
    if (this.isShown(5)) {
      this.apiService.getMeasure(Parameters.loggedUser.id, this.startDate, this.endDate, 4).subscribe({
        next: (r) => {
          this.segnaleTemp = r;
          this.loadingTemp = false;
        },
        error: (err: HttpErrorResponse) => {
          this.loadingTemp = false;
          this.messageService.showError(err);
        }
      })
    } else {
      this.loadingTemp = false;
    }
  }

  private loadMeasures(): void {
    this.selectedMeasures = [];

    Parameters.configuration.shownMeasures.forEach(m => {
      const found = this.findMeasure(this.availableMeasures, m);
      if (found !== undefined) this.selectedMeasures.push(found);
    })
  }

  private setReferenceTime(): void {
    this.endDate = new Date(this.startDate.getTime());
    this.endDate.setHours(this.endDate.getHours() + 1);

    this.xMin = this.startDate.getTime();
    this.xMax = this.endDate.getTime();
  }
}
